import {PopupDropdown} from '@wandb/weave/common/components/PopupDropdown';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {FC, useContext, useMemo, useState} from 'react';

import {ProjectData} from '../../state/graphql/projectPageQuery';
import {
  EntityProjectPermissions,
  useViewerHasPermissionForEntityProject,
} from '../../util/entityProjectPermissions';
import {StarredProjectsContext} from '../../util/StarredProjectsContextProvider';
import {DeleteProjectModal} from './DeleteProjectModal';
import * as S from './ProjectActionMenu.styles';
import ProjectRenameModal from './ProjectRenameModal';

type ProjectActionMenuProps = {
  project: Pick<ProjectData, 'name' | 'entityName' | 'id'>;
  onDeleted?: () => void;
};

export const ProjectActionMenu: FC<ProjectActionMenuProps> = ({
  project,
  onDeleted,
}) => {
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // HACK: Note that the following two hook calls pass an empty string if the
  //       menu below hasn't been opened yet. If an empty string is passed to
  //       `useViewerHasPermissionForEntityProject`, it will skip making the gql
  //       query to the server. This has the effect of making these two calls
  //       lazy, invoked only when the menu is opened. This prevents the list
  //       of projects from lagging the UI, because it's trying to get delete &
  //       rename permissions for every project, which is quite CPU expensive.
  //
  //       Ideally there would be some way of letting PopupDropdown have richer
  //       content that can be evaulated lazily on-mount. That doesn't exist in
  //       the component API at the time of writing, hence this workaround:
  //
  const {hasPermission: canDeleteProject} =
    useViewerHasPermissionForEntityProject(
      project.name,
      hasBeenOpened ? project.entityName : '',
      EntityProjectPermissions.DeleteProject
    );
  const {hasPermission: canRenameProject} =
    useViewerHasPermissionForEntityProject(
      project.name,
      hasBeenOpened ? project.entityName : '',
      EntityProjectPermissions.RenameProject
    );
  const {refetch: refetchStarredProjects} = useContext(StarredProjectsContext);

  const options = useMemo(() => {
    if (!hasBeenOpened) {
      return [];
    }
    return [
      {
        text: (
          <span>
            Rename
            {!canRenameProject && <i> (admin only)</i>}
          </span>
        ),
        icon: 'wbic-ic-edit',
        onClick: () => setIsRenameModalOpen(true),
        disabled: !canRenameProject,
      },
      {
        text: (
          <span>
            Delete
            {!canDeleteProject && <i> (admin only)</i>}
          </span>
        ),
        icon: 'wbic-ic-delete',
        onClick: () => setIsDeleteModalOpen(true),
        disabled: !canDeleteProject,
      },
    ];
  }, [canDeleteProject, canRenameProject, hasBeenOpened]);

  return (
    <Tailwind>
      <S.Wrapper className="min-h-[32px]">
        <ProjectRenameModal
          open={isRenameModalOpen}
          targetProject={{name: project.name, entityName: project.entityName}}
          onClose={() => setIsRenameModalOpen(false)}
          onRename={() => window.location.reload()}
        />
        <PopupDropdown
          offset={'10px, -10px'}
          position="bottom right"
          trigger={
            <Button
              className="hidden group-hover:flex"
              size="medium"
              variant="ghost"
              icon="overflow-horizontal"
              tabIndex={0}
            />
          }
          options={options}
          onOpen={() => setHasBeenOpened(true)}
        />
        <DeleteProjectModal
          isOpen={isDeleteModalOpen}
          project={project}
          setIsModalOpen={setIsDeleteModalOpen}
          handleModalClose={() => {
            setIsDeleteModalOpen(false);
            refetchStarredProjects();
            onDeleted?.();
          }}
        />
      </S.Wrapper>
    </Tailwind>
  );
};
